import { create } from "zustand";
import {
  fetchWatchlistDefaultFilterValues,
  fetchWatchlistFilterOptions,
  fetchWatchlistGovStateOptions,
  fetchWatchlistMoodyRatingOptions,
  fetchWatchlistSnpRatingOptions,
} from "../http/api";

const DEFAULT_FILTER_DATA = {
  bondType: "",
  securityCharge: "",
  side: "",
  couponMin: "",
  couponMax: "",
  maturityMin: "",
  maturityMax: "",
  priceMin: "",
  priceMax: "",
  yieldMin: "",
  yieldMax: "",
  duration: "",
  durationComparison: "",
  minimumRating: "",
  minimumRatingMoody: "",
  zeroCoupon: "",
  perpetual: "",
  callable: "",
  state: "",
  insured: "",
  taxable: "",
  paymentFrequency: "",
  incomeSource: "",
  altMinTax: "",
  sector: "",
  callStartDate: "",
  nextCallDate: "",
  issuer: "",
  conventionMethod: "",
  couponType: "",
  currency: "",
  issueCurrency: "",
  subType: "",
};

const WATCHLIST_BOND_TYPE_TO_API = {
  Gov: "GOV",
  GOV: "GOV",
  Treasury: "GOV",
  Corp: "CORP",
  CORP: "CORP",
  Corporate: "CORP",
  Corporates: "CORP",
  Muni: "MUNI",
  MUNI: "MUNI",
  Municipal: "MUNI",
  Municipals: "MUNI",
};

const DEFAULT_FILTER_OPTIONS = {
  spRatingOptions: [],
  moodyRatingOptions: [],
  issuerOptions: [],
  paymentFrequencyOptions: [],
  sectorOptions: [],
  conventionMethodOptions: [],
  couponTypeOptions: [],
  currencyOptions: [],
  govStateOptions: [],
  incomeSourceOptions: [],
  issueCurrencyOptions: [],
  securityChargeOptions: [],
  subTypeOptions: [],
};

let sharedWatchlistFilterOptionsPromise = null;

const buildBaseFilterData = (overrides = {}) => ({
  ...DEFAULT_FILTER_DATA,
  ...overrides,
});

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const normalizeRatingOptions = (options = []) =>
  (Array.isArray(options) ? options : []).filter(
    (option) =>
      option
      && option.rating
      && `${option.rating}`.trim() !== ""
      && option.value !== null
      && option.value !== undefined
  );

const normalizeTextOptions = (options = []) =>
  (Array.isArray(options) ? options : [])
    .map((option) => `${option ?? ""}`.trim())
    .filter(Boolean);

const normalizeValueLabelOptions = (options = []) =>
  (Array.isArray(options) ? options : []).reduce((normalizedOptions, option) => {
    if (typeof option === "string") {
      const trimmedOption = option.trim();
      if (trimmedOption) {
        normalizedOptions.push({
          label: trimmedOption,
          value: trimmedOption,
        });
      }
      return normalizedOptions;
    }

    if (!option || typeof option !== "object") {
      return normalizedOptions;
    }

    const value = `${option.value ?? option.code ?? option.abbreviation ?? option.stateCode ?? option.id ?? ""}`.trim();
    const label = `${option.label ?? option.name ?? option.stateName ?? value}`.trim();

    if (!value || !label) {
      return normalizedOptions;
    }

    normalizedOptions.push({ label, value });
    return normalizedOptions;
  }, []);

const normalizeGovStateOptions = (options = []) =>
  normalizeValueLabelOptions(options).map((option) => {
    const value = `${option.value ?? ""}`.trim();
    const label = `${option.label ?? ""}`.trim();

    if (!value || !label || label === value || label.endsWith(`(${value})`)) {
      return option;
    }

    return {
      ...option,
      label: `${label} (${value})`,
    };
  });

const getWatchlistFilterOptionsPayload = (response = {}) =>
  response?.data && typeof response.data === "object" ? response.data : response;

const getWatchlistOptionList = (payload = {}, keys = []) => {
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  return [];
};

const getResponseList = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
};

const getCachedSharedFilterOptions = (filterOptions = {}) => ({
  issuerOptions: normalizeTextOptions(filterOptions.issuerOptions),
  paymentFrequencyOptions: normalizeTextOptions(
    filterOptions.paymentFrequencyOptions
  ),
  sectorOptions: normalizeTextOptions(filterOptions.sectorOptions),
  conventionMethodOptions: normalizeTextOptions(
    filterOptions.conventionMethodOptions
  ),
  couponTypeOptions: normalizeTextOptions(filterOptions.couponTypeOptions),
  currencyOptions: normalizeTextOptions(filterOptions.currencyOptions),
  govStateOptions: normalizeGovStateOptions(filterOptions.govStateOptions),
  incomeSourceOptions: normalizeTextOptions(filterOptions.incomeSourceOptions),
  issueCurrencyOptions: normalizeTextOptions(filterOptions.issueCurrencyOptions),
  securityChargeOptions: normalizeTextOptions(filterOptions.securityChargeOptions),
  subTypeOptions: normalizeTextOptions(filterOptions.subTypeOptions),
});

const hasLoadedAllSharedWatchlistFilterOptions = (state = {}) =>
  Boolean(
    state.hasLoadedWatchlistFilterOptions
    && state.hasLoadedSpRatingOptions
    && state.hasLoadedMoodyRatingOptions
    && state.hasLoadedGovStateOptions
  );

const loadSharedWatchlistFilterOptions = (token) => {
  if (!sharedWatchlistFilterOptionsPromise) {
    sharedWatchlistFilterOptionsPromise = Promise.allSettled([
      fetchWatchlistFilterOptions(token),
      fetchWatchlistSnpRatingOptions(token),
      fetchWatchlistMoodyRatingOptions(token),
      fetchWatchlistGovStateOptions(token),
    ]).finally(() => {
      sharedWatchlistFilterOptionsPromise = null;
    });
  }

  return sharedWatchlistFilterOptionsPromise;
};

const resolveSharedWatchlistFilterOptionsState = (
  state = {},
  sharedOptionsResults = null
) => {
  let watchlistOptionLists = getCachedSharedFilterOptions(state.filterOptions);
  let spRatingOptions = normalizeRatingOptions(state.filterOptions.spRatingOptions);
  let moodyRatingOptions = normalizeRatingOptions(
    state.filterOptions.moodyRatingOptions
  );
  let hasLoadedWatchlistFilterOptions = Boolean(
    state.hasLoadedWatchlistFilterOptions
  );
  let hasLoadedSpRatingOptions = Boolean(state.hasLoadedSpRatingOptions);
  let hasLoadedMoodyRatingOptions = Boolean(state.hasLoadedMoodyRatingOptions);
  let hasLoadedGovStateOptions = Boolean(state.hasLoadedGovStateOptions);

  if (sharedOptionsResults) {
    const [
      filterOptionsResult,
      spRatingsResult,
      moodyRatingsResult,
      govStateOptionsResult,
    ] = sharedOptionsResults;

    if (filterOptionsResult?.status === "fulfilled") {
      watchlistOptionLists = normalizeWatchlistFilterOptions(
        filterOptionsResult.value
      );
      hasLoadedWatchlistFilterOptions = true;
    } else if (!hasLoadedWatchlistFilterOptions) {
      console.error(
        "[watchlist] Failed to load watchlist filter options:",
        filterOptionsResult?.reason
      );
    }

    if (spRatingsResult?.status === "fulfilled") {
      spRatingOptions = normalizeRatingOptions(
        getResponseList(spRatingsResult.value)
      );
      hasLoadedSpRatingOptions = true;
    } else if (!hasLoadedSpRatingOptions) {
      console.error(
        "[watchlist] Failed to load watchlist S&P rating options:",
        spRatingsResult?.reason
      );
    }

    if (moodyRatingsResult?.status === "fulfilled") {
      moodyRatingOptions = normalizeRatingOptions(
        getResponseList(moodyRatingsResult.value)
      );
      hasLoadedMoodyRatingOptions = true;
    } else if (!hasLoadedMoodyRatingOptions) {
      console.error(
        "[watchlist] Failed to load watchlist Moody's rating options:",
        moodyRatingsResult?.reason
      );
    }

    if (govStateOptionsResult?.status === "fulfilled") {
      watchlistOptionLists = {
        ...watchlistOptionLists,
        govStateOptions: normalizeGovStateOptions(
          getResponseList(govStateOptionsResult.value)
        ),
      };
      hasLoadedGovStateOptions = true;
    } else if (!hasLoadedGovStateOptions) {
      console.error(
        "[watchlist] Failed to load watchlist state options:",
        govStateOptionsResult?.reason
      );
    }
  }

  return {
    watchlistOptionLists,
    spRatingOptions,
    moodyRatingOptions,
    hasLoadedWatchlistFilterOptions,
    hasLoadedSpRatingOptions,
    hasLoadedMoodyRatingOptions,
    hasLoadedGovStateOptions,
  };
};

const normalizeWatchlistFilterOptions = (response = {}) => {
  const payload = getWatchlistFilterOptionsPayload(response);

  return {
    issuerOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, ["issuerList", "issuer_list"])
    ),
    paymentFrequencyOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, [
        "paymentFrequencyList",
        "payment_frequencyList",
        "payment_frequency_list",
      ])
    ),
    sectorOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, ["sectorList", "sector_list"])
    ),
    conventionMethodOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, [
        "conventionMethodList",
        "convention_methodList",
        "convention_method_list",
      ])
    ),
    couponTypeOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, [
        "couponTypeList",
        "coupon_typeList",
        "coupon_type_list",
      ])
    ),
    currencyOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, ["currencyList", "currency_list"])
    ),
    govStateOptions: normalizeGovStateOptions(
      getWatchlistOptionList(payload, [
        "govStateList",
        "gov_stateList",
        "gov_state_list",
        "stateList",
        "state_list",
      ])
    ),
    incomeSourceOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, [
        "incomeSourceList",
        "income_sourceList",
        "income_source_list",
      ])
    ),
    issueCurrencyOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, [
        "issueCurrencyList",
        "issue_currencyList",
        "issue_currency_list",
      ])
    ),
    securityChargeOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, [
        "securityChargeList",
        "security_chargeList",
        "security_charge_list",
      ])
    ),
    subTypeOptions: normalizeTextOptions(
      getWatchlistOptionList(payload, ["subTypeList", "sub_typeList", "sub_type_list"])
    ),
  };
};

const buildDefaultFilterDataFromResponse = (defaults = {}) => {
  const nextFilterData = {};

  if (hasValue(defaults.minCoupon)) {
    nextFilterData.couponMin = defaults.minCoupon;
  }
  if (hasValue(defaults.maxCoupon)) {
    nextFilterData.couponMax = defaults.maxCoupon;
  }
  if (hasValue(defaults.minMaturity)) {
    nextFilterData.maturityMin = defaults.minMaturity;
  }
  if (hasValue(defaults.maxMaturity)) {
    nextFilterData.maturityMax = defaults.maxMaturity;
  }

  return nextFilterData;
};

const getWatchlistFilterApiType = (bondType) =>
  WATCHLIST_BOND_TYPE_TO_API[bondType] || "GOV";

let latestFilterDefaultsRequestId = 0;
const cachedDefaultFilters = new Map();

export const useMAWatchlistFilterStore = create((set, get) => ({
  userFilterData: { ...DEFAULT_FILTER_DATA },
  appliedFilterData: { ...DEFAULT_FILTER_DATA },
  defaultFilterData: {},
  filterOptions: { ...DEFAULT_FILTER_OPTIONS },
  hasLoadedWatchlistFilterOptions: false,
  hasLoadedSpRatingOptions: false,
  hasLoadedMoodyRatingOptions: false,
  hasLoadedGovStateOptions: false,
  loadingFilterDefaults: false,
  filterDefaultsError: null,
  clearFilterDefaultsError: () => set({ filterDefaultsError: null }),

  setUserFilterData: (data) =>
    set((state) => ({
      userFilterData: { ...state.userFilterData, ...data },
    })),

  replaceUserFilterData: (data = {}) =>
    set({
      userFilterData: buildBaseFilterData(data),
    }),

  setAppliedFilterData: (data) =>
    set((state) => ({
      appliedFilterData: { ...state.appliedFilterData, ...data },
    })),

  replaceAppliedFilterData: (data = {}) =>
    set({
      appliedFilterData: buildBaseFilterData(data),
    }),

  setUserFilterField: (name, value) =>
    set((state) => ({
      userFilterData: { ...state.userFilterData, [name]: value },
    })),

  resetUserFilterData: () =>
    set((state) => ({
      userFilterData: buildBaseFilterData(state.defaultFilterData),
    })),

  resetUserFilterDataKeepBondType: () =>
    set((state) => ({
      userFilterData: buildBaseFilterData({
        ...state.defaultFilterData,
        bondType: state.userFilterData.bondType,
      }),
    })),

  resetAppliedFilterData: () =>
    set({
      appliedFilterData: { ...DEFAULT_FILTER_DATA },
    }),

  syncAppliedFilterDataWithUser: () =>
    set((state) => ({
      appliedFilterData: { ...state.userFilterData },
    })),

  preloadSharedFilterOptions: async (token) => {
    if (!token) {
      return null;
    }

    const state = get();
    if (hasLoadedAllSharedWatchlistFilterOptions(state)) {
      return {
        filterOptions: state.filterOptions,
        cached: true,
      };
    }

    const sharedOptionsResults = await loadSharedWatchlistFilterOptions(token);
    const sharedFilterState = resolveSharedWatchlistFilterOptionsState(
      get(),
      sharedOptionsResults
    );

    set((currentState) => ({
      filterOptions: {
        ...DEFAULT_FILTER_OPTIONS,
        ...sharedFilterState.watchlistOptionLists,
        spRatingOptions: sharedFilterState.spRatingOptions,
        moodyRatingOptions: sharedFilterState.moodyRatingOptions,
      },
      hasLoadedWatchlistFilterOptions:
        sharedFilterState.hasLoadedWatchlistFilterOptions,
      hasLoadedSpRatingOptions: sharedFilterState.hasLoadedSpRatingOptions,
      hasLoadedMoodyRatingOptions: sharedFilterState.hasLoadedMoodyRatingOptions,
      hasLoadedGovStateOptions: sharedFilterState.hasLoadedGovStateOptions,
      filterDefaultsError: currentState.filterDefaultsError,
    }));

    return {
      filterOptions: {
        ...DEFAULT_FILTER_OPTIONS,
        ...sharedFilterState.watchlistOptionLists,
        spRatingOptions: sharedFilterState.spRatingOptions,
        moodyRatingOptions: sharedFilterState.moodyRatingOptions,
      },
    };
  },

  loadFilterDefaults: async (
    token,
    bondType = get().userFilterData.bondType,
    { skipUserFilterDataUpdate = false } = {}
  ) => {
    if (!token) {
      sharedWatchlistFilterOptionsPromise = null;
      cachedDefaultFilters.clear();
      set({
        filterOptions: { ...DEFAULT_FILTER_OPTIONS },
        defaultFilterData: {},
        userFilterData: { ...DEFAULT_FILTER_DATA },
        appliedFilterData: { ...DEFAULT_FILTER_DATA },
        hasLoadedWatchlistFilterOptions: false,
        hasLoadedSpRatingOptions: false,
        hasLoadedMoodyRatingOptions: false,
        hasLoadedGovStateOptions: false,
        loadingFilterDefaults: false,
        filterDefaultsError: null,
      });
      return null;
    }

    set({ loadingFilterDefaults: true, filterDefaultsError: null });
    const requestId = ++latestFilterDefaultsRequestId;

    try {
      const state = get();
      const resolvedBondType = bondType || DEFAULT_FILTER_DATA.bondType;
      const apiType = getWatchlistFilterApiType(resolvedBondType);

      if (!cachedDefaultFilters.has(apiType)) {
        cachedDefaultFilters.set(
          apiType,
          fetchWatchlistDefaultFilterValues(token, { type: apiType })
        );
      }

      const shouldLoadSharedOptions = !(
        state.hasLoadedWatchlistFilterOptions
        && state.hasLoadedSpRatingOptions
        && state.hasLoadedMoodyRatingOptions
        && state.hasLoadedGovStateOptions
      );

      let defaults;
      let sharedOptionsResults = null;

      try {
        [defaults, sharedOptionsResults] = await Promise.all([
          cachedDefaultFilters.get(apiType),
          shouldLoadSharedOptions
            ? loadSharedWatchlistFilterOptions(token)
            : Promise.resolve(null),
        ]);
      } catch (error) {
        cachedDefaultFilters.delete(apiType);
        throw error;
      }

      const sharedFilterState = resolveSharedWatchlistFilterOptionsState(
        state,
        sharedOptionsResults
      );

      const defaultFilterData = buildDefaultFilterDataFromResponse(defaults);
      const filterOptions = {
        ...DEFAULT_FILTER_OPTIONS,
        ...sharedFilterState.watchlistOptionLists,
        spRatingOptions:
          sharedFilterState.spRatingOptions.length > 0
            ? sharedFilterState.spRatingOptions
            : normalizeRatingOptions(defaults?.spRatingMap),
        moodyRatingOptions:
          sharedFilterState.moodyRatingOptions.length > 0
            ? sharedFilterState.moodyRatingOptions
            : normalizeRatingOptions(defaults?.moodyRatingMap),
      };

      if (requestId !== latestFilterDefaultsRequestId) {
        return {
          defaults,
          filterOptions,
          defaultFilterData,
          ignored: true,
        };
      }

      set((state) => ({
        defaultFilterData,
        filterOptions,
        hasLoadedWatchlistFilterOptions:
          sharedFilterState.hasLoadedWatchlistFilterOptions,
        hasLoadedSpRatingOptions: sharedFilterState.hasLoadedSpRatingOptions,
        hasLoadedMoodyRatingOptions:
          sharedFilterState.hasLoadedMoodyRatingOptions,
        hasLoadedGovStateOptions: sharedFilterState.hasLoadedGovStateOptions,
        ...(skipUserFilterDataUpdate
          ? {}
          : {
              userFilterData: buildBaseFilterData({
                ...defaultFilterData,
                bondType: resolvedBondType,
              }),
            }),
        loadingFilterDefaults: false,
        filterDefaultsError: null,
      }));

      return {
        defaults,
        filterOptions,
        defaultFilterData,
      };
    } catch (error) {
      if (requestId !== latestFilterDefaultsRequestId) {
        return null;
      }

      const message = error?.message || "Failed to load watchlist filter defaults";
      set({ loadingFilterDefaults: false, filterDefaultsError: message });
      throw error;
    }
  },
}));
